import React, { useState, useEffect } from 'react';
import { 
    Clock, Calendar, Bell, Send, CheckCircle, AlertTriangle, 
    Users, Phone, Mail, MessageSquare, Settings
} from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import { useNotifications } from './contexts/NotificationContext';
import apiService from './apiService';

const AppointmentReminders = ({ onNavigate }) => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const { notifyAppointmentReminder } = useNotifications();
    const [loading, setLoading] = useState(true);
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    const [reminderSettings, setReminderSettings] = useState({
        enabled: true,
        advanceTime: 24, // hours
        methods: {
            inApp: true,
            email: false,
            sms: false
        },
        templates: {
            inApp: "You have an appointment with {patientName} at {appointmentTime}",
            email: "Reminder: You have an appointment with {patientName} at {appointmentTime} in {department}",
            sms: "Appointment reminder: {patientName} at {appointmentTime}"
        }
    });
    const [selectedAppointments, setSelectedAppointments] = useState([]);
    const [customMessage, setCustomMessage] = useState('');

    useEffect(() => {
        fetchUpcomingAppointments();
        loadReminderSettings();
    }, []);

    const fetchUpcomingAppointments = async () => {
        setLoading(true);
        try {
            // Get appointments for the next 7 days
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + 7);
            
            const appointments = await apiService.getAppointments({
                organizationId: currentUser.organizationId,
                startDate: new Date().toISOString(),
                endDate: endDate.toISOString()
            });

            // Filter for upcoming appointments
            const upcoming = appointments.filter(apt => {
                const appointmentTime = new Date(apt.appointmentTime);
                const now = new Date();
                return appointmentTime > now && apt.status === 'scheduled';
            }).sort((a, b) => new Date(a.appointmentTime) - new Date(b.appointmentTime));

            setUpcomingAppointments(upcoming);
        } catch (error) {
            console.error('Error fetching upcoming appointments:', error);
            showAlert('Failed to load upcoming appointments', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadReminderSettings = () => {
        const saved = localStorage.getItem('appointmentReminderSettings');
        if (saved) {
            setReminderSettings(JSON.parse(saved));
        }
    };

    const saveReminderSettings = (newSettings) => {
        setReminderSettings(newSettings);
        localStorage.setItem('appointmentReminderSettings', JSON.stringify(newSettings));
    };

    const handleAppointmentSelect = (appointmentId) => {
        setSelectedAppointments(prev => 
            prev.includes(appointmentId) 
                ? prev.filter(id => id !== appointmentId)
                : [...prev, appointmentId]
        );
    };

    const handleSelectAll = () => {
        if (selectedAppointments.length === upcomingAppointments.length) {
            setSelectedAppointments([]);
        } else {
            setSelectedAppointments(upcomingAppointments.map(apt => apt.id));
        }
    };

    const sendReminders = async () => {
        if (selectedAppointments.length === 0) {
            showAlert('Please select appointments to send reminders for', 'warning');
            return;
        }

        try {
            const selectedApts = upcomingAppointments.filter(apt => 
                selectedAppointments.includes(apt.id)
            );

            for (const appointment of selectedApts) {
                // Send in-app notification
                if (reminderSettings.methods.inApp) {
                    notifyAppointmentReminder(appointment);
                }

                // Here you would integrate with email/SMS services
                // For now, we'll just log the action
                console.log('Reminder sent for appointment:', appointment.id);
            }

            showAlert(`Reminders sent for ${selectedAppointments.length} appointments`, 'success');
            setSelectedAppointments([]);
        } catch (error) {
            console.error('Error sending reminders:', error);
            showAlert('Failed to send reminders', 'error');
        }
    };

    const formatAppointmentTime = (dateTimeString) => {
        const date = new Date(dateTimeString);
        return {
            date: date.toLocaleDateString('en-US', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric' 
            }),
            time: date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
            })
        };
    };

    const getTimeUntilAppointment = (dateTimeString) => {
        const appointmentTime = new Date(dateTimeString);
        const now = new Date();
        const diffMs = appointmentTime - now;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) {
            return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
        } else if (diffHours > 0) {
            return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
        } else {
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
        }
    };

    const getUrgencyColor = (dateTimeString) => {
        const appointmentTime = new Date(dateTimeString);
        const now = new Date();
        const diffHours = (appointmentTime - now) / (1000 * 60 * 60);

        if (diffHours < 2) return 'text-red-600 bg-red-100';
        if (diffHours < 24) return 'text-yellow-600 bg-yellow-100';
        return 'text-green-600 bg-green-100';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Appointment Reminders</h2>
                    <p className="text-gray-600">Manage and send appointment reminders</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={fetchUpcomingAppointments}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                    >
                        <Calendar className="w-4 h-4" />
                        Refresh
                    </button>
                    <button
                        onClick={() => onNavigate('settings')}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-600"
                    >
                        <Settings className="w-4 h-4" />
                        Settings
                    </button>
                </div>
            </div>

            {/* Reminder Settings */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Reminder Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reminder Methods
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={reminderSettings.methods.inApp}
                                    onChange={(e) => saveReminderSettings({
                                        ...reminderSettings,
                                        methods: { ...reminderSettings.methods, inApp: e.target.checked }
                                    })}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-600">In-App Notifications</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={reminderSettings.methods.email}
                                    onChange={(e) => saveReminderSettings({
                                        ...reminderSettings,
                                        methods: { ...reminderSettings.methods, email: e.target.checked }
                                    })}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-600">Email</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={reminderSettings.methods.sms}
                                    onChange={(e) => saveReminderSettings({
                                        ...reminderSettings,
                                        methods: { ...reminderSettings.methods, sms: e.target.checked }
                                    })}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-600">SMS</span>
                            </label>
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Advance Time (Hours)
                        </label>
                        <select
                            value={reminderSettings.advanceTime}
                            onChange={(e) => saveReminderSettings({
                                ...reminderSettings,
                                advanceTime: parseInt(e.target.value)
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value={1}>1 hour</option>
                            <option value={2}>2 hours</option>
                            <option value={4}>4 hours</option>
                            <option value={12}>12 hours</option>
                            <option value={24}>24 hours</option>
                            <option value={48}>48 hours</option>
                        </select>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Custom Message
                        </label>
                        <textarea
                            rows={3}
                            placeholder="Optional custom message for reminders..."
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Upcoming Appointments */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            Upcoming Appointments ({upcomingAppointments.length})
                        </h3>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={handleSelectAll}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                {selectedAppointments.length === upcomingAppointments.length ? 'Deselect All' : 'Select All'}
                            </button>
                            <button
                                onClick={sendReminders}
                                disabled={selectedAppointments.length === 0}
                                className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Send className="w-4 h-4" />
                                Send Reminders ({selectedAppointments.length})
                            </button>
                        </div>
                    </div>
                </div>
                
                <div className="p-6">
                    {upcomingAppointments.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>No upcoming appointments found</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {upcomingAppointments.map((appointment) => {
                                const timeInfo = formatAppointmentTime(appointment.appointmentTime);
                                const timeUntil = getTimeUntilAppointment(appointment.appointmentTime);
                                const urgencyColor = getUrgencyColor(appointment.appointmentTime);
                                
                                return (
                                    <div 
                                        key={appointment.id} 
                                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                            selectedAppointments.includes(appointment.id) 
                                                ? 'border-blue-500 bg-blue-50' 
                                                : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => handleAppointmentSelect(appointment.id)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAppointments.includes(appointment.id)}
                                                    onChange={() => handleAppointmentSelect(appointment.id)}
                                                    className="w-4 h-4 text-blue-600"
                                                />
                                                
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h4 className="font-semibold text-gray-900">
                                                            {appointment.patient?.name || 'Unknown Patient'}
                                                        </h4>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgencyColor}`}>
                                                            {timeUntil}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                                        <div className="flex items-center gap-1">
                                                            <Calendar className="w-4 h-4" />
                                                            {timeInfo.date} at {timeInfo.time}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Users className="w-4 h-4" />
                                                            {appointment.department}
                                                        </div>
                                                        {appointment.patient?.phone && (
                                                            <div className="flex items-center gap-1">
                                                                <Phone className="w-4 h-4" />
                                                                {appointment.patient.phone}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {appointment.notes && (
                                                        <p className="text-sm text-gray-500 mt-2">
                                                            {appointment.notes}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    appointment.priority === 'high' ? 'bg-red-100 text-red-800' :
                                                    appointment.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-green-100 text-green-800'
                                                }`}>
                                                    {appointment.priority || 'normal'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppointmentReminders;
